import { ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useMailConnections, useMailSearch, useMailThread } from '@/hooks/useMail'
import type { MailMessage } from '@/types/api/mail'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/date'

import type { EmailMessageValue } from '../utils/emailMessage'

interface Props {
  /** Search the box opens with (the field's default search filled from the record). */
  initialQuery: string
  /** Mailbox to start on, e.g. the one the field already points at. */
  initialConnectionId?: string
  onClose: () => void
  onPick: (value: EmailMessageValue) => void
}

/** Searches a connected mailbox and returns the chosen email, or one exact message of a thread. */
export function EmailPickerDialog({ initialQuery, initialConnectionId, onClose, onPick }: Props) {
  const { data: connections, isLoading: loadingConnections } = useMailConnections()
  const usable = (connections ?? []).filter((c) => c.status === 'ACTIVE')
  const [connectionId, setConnectionId] = useState<string | null>(null)
  const [query, setQuery] = useState(initialQuery)
  const [selected, setSelected] = useState<MailMessage | null>(null)
  const [expandedThread, setExpandedThread] = useState<string | null>(null)
  const debounced = useDebouncedValue(query, 400)

  // Once mailboxes load, start on the linked email's mailbox if this user may read it, else the first one.
  useEffect(() => {
    if (connectionId || usable.length === 0) return
    setConnectionId(usable.some((c) => c.id === initialConnectionId) ? initialConnectionId! : usable[0].id)
  }, [connectionId, usable, initialConnectionId])

  const search = useMailSearch(connectionId, debounced)
  const messages = search.data?.pages.flatMap((page) => page.items) ?? []

  const choose = () => {
    if (!selected || !connectionId) return
    onPick({
      connectionId,
      messageId: selected.messageId,
      threadId: selected.threadId,
      internetMessageId: selected.internetMessageId,
      references: selected.references,
      subject: selected.subject,
      from: selected.from,
      replyTo: selected.replyTo,
      to: selected.to,
      date: selected.date,
    })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose an email</DialogTitle>
          <DialogDescription>Pick the email this record is about. Replies from automations go to its conversation.</DialogDescription>
        </DialogHeader>

        {!loadingConnections && usable.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-neutral-500">
            No mailbox is connected. Connect one under <a className="underline" href="/settings?tab=mailboxes">Settings → Mailboxes</a>, then come back.
          </p>
        ) : (
          <div className="min-w-0 space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={connectionId ?? ''} onValueChange={(value) => { setConnectionId(value); setSelected(null) }}>
                <SelectTrigger aria-label="Mailbox" className="sm:w-64">
                  <SelectValue placeholder="Mailbox" />
                </SelectTrigger>
                <SelectContent>
                  {usable.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.emailAddress}{c.scope === 'WORKSPACE' ? ' (shared)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search emails (for example from:jane@acme.com)"
                  aria-label="Search emails"
                  className="w-full rounded-lg border border-neutral-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="max-h-96 space-y-1 overflow-y-auto">
              {search.isLoading && <p className="flex items-center justify-center gap-2 py-6 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" />Searching…</p>}
              {search.isError && <p className="py-6 text-center text-sm text-destructive-600">Could not read this mailbox. It may need to be reconnected.</p>}
              {search.isSuccess && messages.length === 0 && <p className="py-6 text-center text-sm text-neutral-500">No matching emails</p>}
              {messages.map((message) => (
                <div key={message.messageId}>
                  <div className="flex items-start gap-1">
                    <button
                      type="button"
                      aria-label={expandedThread === message.threadId ? 'Hide conversation' : 'Show conversation'}
                      disabled={!message.threadId}
                      onClick={() => setExpandedThread(expandedThread === message.threadId ? null : (message.threadId ?? null))}
                      className="mt-2 rounded p-1 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30"
                    >
                      {expandedThread === message.threadId ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <MessageRow message={message} selected={selected?.messageId === message.messageId} onSelect={() => setSelected(message)} />
                  </div>
                  {expandedThread && expandedThread === message.threadId && (
                    <ThreadMessages
                      connectionId={connectionId}
                      threadId={expandedThread}
                      selectedId={selected?.messageId ?? null}
                      onSelect={setSelected}
                    />
                  )}
                </div>
              ))}
              {search.hasNextPage && (
                <Button variant="ghost" size="sm" className="w-full" disabled={search.isFetchingNextPage} onClick={() => search.fetchNextPage()}>
                  {search.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={choose} disabled={!selected}>Use this email</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MessageRow({ message, selected, onSelect }: { message: MailMessage; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'min-w-0 flex-1 rounded-lg border px-3 py-2 text-left text-sm',
        selected ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:bg-neutral-50'
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate font-medium">{message.subject || '(no subject)'}</span>
        {message.date && <span className="shrink-0 text-xs text-neutral-500">{formatDateTime(message.date)}</span>}
      </div>
      <p className="truncate text-xs text-neutral-600">{message.from}</p>
      {message.snippet && <p className="truncate text-xs text-neutral-400">{message.snippet}</p>}
    </button>
  )
}

function ThreadMessages({ connectionId, threadId, selectedId, onSelect }: {
  connectionId: string | null
  threadId: string
  selectedId: string | null
  onSelect: (message: MailMessage) => void
}) {
  const { data, isLoading, isError } = useMailThread(connectionId, threadId)
  return (
    <div className="ml-7 mt-1 space-y-1 border-l pl-3">
      {isLoading && <p className="py-2 text-xs text-neutral-500">Loading conversation…</p>}
      {isError && <p className="py-2 text-xs text-destructive-600">Could not load the conversation</p>}
      {data?.map((message) => (
        <MessageRow key={message.messageId} message={message} selected={selectedId === message.messageId} onSelect={() => onSelect(message)} />
      ))}
    </div>
  )
}
